import { get } from '@grubhubprod/umami-library/dist/nodash/get';
import {
  Claim,
  Credential,
} from '@grubhubprod/order-taking-client-sdk/dist/sdk/types/auth';
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useLocation, useHistory, useParams } from 'react-router-dom';
import { CorpUIResponse } from 'src/types/Corp';
import { sdk } from 'src/utilities/sdk';
import { getWindow } from 'src/utilities/window';
import { getHomePath } from 'src/constants/urls';
import { useFeatureFlag } from 'src/hooks/useFeatureFlag';
import { FeatureFlag } from 'src/types/FeatureFlag';
import { camelizeResponseData } from 'src/utilities/http-data-casing';

export interface IEnterpriseData {
  isInternalAdminUser: boolean;
  isFinancialAdminUser: boolean;
  isOnlyCorpAdminUser: boolean;
  isInternalAdminPath: boolean;
  isParentAdminUser: boolean;
  isParentAdminPath: boolean;
}

export interface IAuthContext {
  user: Credential;
  corpId: string;
  enterpriseData: IEnterpriseData;
  corpData: CorpUIResponse;
  signOut: () => void;
  checkUserAuthentication: () => void;
  redirectToCorpAdminSite: () => void;
  redirectToHomePage: (corpId: string) => void;
  allUserCorps: AdminCorpsBySignupType;
  hasAuthError: boolean;
}

interface IAuthProviderProps {
  children: ReactNode;
}
const AuthContext = createContext<IAuthContext>(null);

export const HAS_NO_BASIC_ACCOUNT_STATUS = 204;
export enum BASIC_ACCOUNT_STATUS {
  ACTIVE = 'ACTIVE',
  SIGN_UP_IN_PROGRESS = 'SIGN_UP_IN_PROGRESS',
}
export enum CORP_CLAIMS {
  INTERNAL_ADMIN = 'internal_admin',
  CORP_DINER = 'corp_diner',
  CORP_FINANCIAL_ADMIN = 'corp_financial_admin',
  CORP_ADMIN = 'corp_admin',
  CORP_PARENT_ADMIN = 'corp_admin_group', // this difference in variable name vs claim is to reduce confusion since this is a parent admins claim
}

export enum CORP_SIGN_UP_TYPE {
  BASIC = 'BASIC',
  PREMIUM = 'PREMIUM',
}

export enum CORP_USER_ROLE {
  CORP_DINER = 'CORP_DINER',
  CORP_ADMIN = 'CORP_ADMIN',
  CORP_FINANCIAL_ADMIN = 'CORP_FINANCIAL_ADMIN',
  CORP_ADMIN_AND_FINANCIAL_ADMIN = 'CORP_ADMIN_AND_FINANCIAL_ADMIN',
  CORP_PARENT_ADMIN = 'CORP_PARENT_ADMIN',
}

export interface ICorpByRole {
  sign_up_type: CORP_SIGN_UP_TYPE;
  role: CORP_USER_ROLE;
  diner_id: string;
  effective_date: string;
  owner_id: string;
  owner_name: string;
  client_id?: string;
  account_status?: BASIC_ACCOUNT_STATUS;
}
export type AdminCorpsBySignupType = {
  [SignupType in CORP_SIGN_UP_TYPE]: ICorpByRole[];
};

function AuthProvider({ children }: IAuthProviderProps): JSX.Element {
  const { pathname, search } = useLocation();
  const { push } = useHistory();
  const params = useParams<{ corpid: string }>();
  const { corpid: urlCorpId } = params;
  const window = getWindow();
  // Pause Self-Service Sign-Up https://jira.grubhub.com/browse/CEC-5282
  const { value: blockSelfServiceSignup } = useFeatureFlag(
    FeatureFlag.BLOCK_SELF_SERVICE_SIGNUP
  );

  const asPath = pathname + search;

  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // This replaces the isReady logic from next/routing.
    // This deffers the checking for auth and fetching user data
    // I left this as it is because there was a higher chance of regression if I removed it completely
    // since this influences the logic from verifyPermissions with the user access
    if (pathname) setIsReady(true);
  }, [pathname]);

  const [user, setUser] = useState<Credential>();
  const [corpId, setCorpId] = useState(null);
  const [corpData, setCorpData] = useState<CorpUIResponse>(null);
  const [allUserCorps, setAllUserCorps] = useState<AdminCorpsBySignupType>({
    BASIC: [],
    PREMIUM: [],
  });

  const [hasAuthError, setHasAuthError] = useState(false);
  const [isUserAuthorizedForCurrentPath, setIsUserAuthorizedForCurrentPath] =
    useState(false);

  const [enterpriseData, setEnterpriseData] = useState<IEnterpriseData>({
    isFinancialAdminUser: false,
    isInternalAdminPath: false,
    isInternalAdminUser: false,
    isOnlyCorpAdminUser: false,
    isParentAdminUser: false,
    isParentAdminPath: false,
  });

  const isSignupPath = pathname.indexOf('/signup/step') !== -1;
  const isBlockingSignupPath = pathname.indexOf('/signup/info') !== -1;
  const isManagementPath = pathname.indexOf('/management') !== -1;
  const isEnterpriseManagementPath =
    pathname.indexOf(`/enterprise/${urlCorpId}`) !== -1;

  const isCurrentPathInternalAdmin =
    pathname.indexOf('/enterprise/toggles') !== -1 ||
    pathname.indexOf('/enterprise/internaladmins') !== -1 ||
    pathname.includes('/enterprise/corps');

  const isCurrentPathParentAdmin =
    pathname.indexOf('/enterprise/corporations') !== -1 ||
    pathname.indexOf('/enterprise/parents') !== -1;

  async function checkUserAuthentication() {
    try {
      const isSignedIn = await isUserSignedIn();

      if (isSignedIn) {
        await verifyPermissions();
      } else if (blockSelfServiceSignup && isBlockingSignupPath) {
        setIsUserAuthorizedForCurrentPath(true);
      } else {
        redirectToSignInPage();
      }
    } catch (error) {
      redirectToSignInPage();
    }
  }

  async function verifyPermissions(): Promise<void> {
    let corpData;
    const user = sdk.view.credentials.credential();
    const isInternalAdmin = !!sdk.view.credentials.claim(
      CORP_CLAIMS.INTERNAL_ADMIN
    );
    const isParentAdmin = !!sdk.view.credentials.claim(
      CORP_CLAIMS.CORP_PARENT_ADMIN
    );

    const corpAdminClaims = getCorpAndFinancialAdminClaims();
    let corpId;
    let allCorpsUserIsAdminOf: AdminCorpsBySignupType = {
      BASIC: [],
      PREMIUM: [],
    };
    let isBasicAdminBySignUpType;
    let isPremiumAdminBySignUpType;

    if (!isInternalAdmin && corpAdminClaims.length > 0) {
      try {
        allCorpsUserIsAdminOf = await getAllUserCorpsBySignupType(user.ud_id);
      } catch (error) {
        try {
          // Looking up urlCorpId in claims
          const isUrlCorpIdInClaims = corpAdminClaims.some(
            (claim) => claim.claim_id === urlCorpId
          );
          // if there is not urlCorpId in claims take the first claim_id from the list of claims
          corpId = isUrlCorpIdInClaims
            ? urlCorpId
            : corpAdminClaims[0].claim_id;

          corpData = await getCorpData(corpId);
          isBasicAdminBySignUpType =
            corpData.signUpType === CORP_SIGN_UP_TYPE.BASIC;
          isPremiumAdminBySignUpType =
            corpData.signUpType === CORP_SIGN_UP_TYPE.PREMIUM;
          // push the corp data to Premium corp list when it is premium admin
          if (isPremiumAdminBySignUpType) {
            allCorpsUserIsAdminOf.PREMIUM.push({
              client_id: corpData.clientId,
              diner_id: user.ud_id,
              effective_date: corpData.effectiveDate,
              owner_id: corpData.id,
              owner_name: corpData.name,
              role: corpData.adminUiProvider as CORP_USER_ROLE,
              sign_up_type: corpData.signUpType as CORP_SIGN_UP_TYPE,
            });
          }
          // push the corp data to Basic corp list when it is basic admin
          if (isBasicAdminBySignUpType) {
            allCorpsUserIsAdminOf.BASIC.push({
              account_status: corpData.accountStatus as BASIC_ACCOUNT_STATUS,
              diner_id: user.ud_id,
              effective_date: corpData.effectiveDate,
              owner_id: corpData.id,
              owner_name: corpData.name,
              role: corpData.adminUiProvider as CORP_USER_ROLE,
              sign_up_type: corpData.signUpType as CORP_SIGN_UP_TYPE,
            });
          }
          // if there is no urlCorpId in claims it needs to reload page with proper basic id when it is Basic admin
          if (isBasicAdminBySignUpType && !isUrlCorpIdInClaims) {
            reloadManagementPage(allCorpsUserIsAdminOf.BASIC[0].owner_id);
          }

          // if there is no urlCorpId in claims it needs to reload page with proper premium id when it is Premium admin
          if (isPremiumAdminBySignUpType && !isUrlCorpIdInClaims) {
            redirectToHomePage(allCorpsUserIsAdminOf.PREMIUM[0].owner_id);
          }
        } catch (e) {
          setHasAuthError(true);
          return;
        }
      }
    }

    const userHasAccessToCurrentBasicCorp = isCorpIdInArray(
      allCorpsUserIsAdminOf.BASIC,
      urlCorpId as string
    );

    const isPremiumAdmin =
      (isPremiumAdminBySignUpType ||
        allCorpsUserIsAdminOf.PREMIUM.length > 0) &&
      !isInternalAdmin;

    const isBasicAdmin =
      isBasicAdminBySignUpType ||
      allCorpsUserIsAdminOf.BASIC.some(
        ({ account_status }) => account_status === BASIC_ACCOUNT_STATUS.ACTIVE
      );

    const isNonCorpUser = !(isInternalAdmin || isPremiumAdmin || isBasicAdmin);

    if (isSignupPath) {
      // internal admins cannot signup a basic corp
      if (isInternalAdmin) {
        redirectToCorpAdminSite();
        return;
      }

      const basicCorpInProgress = allCorpsUserIsAdminOf.BASIC.find(
        (corp) =>
          corp.sign_up_type === CORP_SIGN_UP_TYPE.BASIC &&
          corp.account_status === BASIC_ACCOUNT_STATUS.SIGN_UP_IN_PROGRESS
      );

      // set the corp ID to that of the corp currently being created
      if (basicCorpInProgress) {
        corpId = basicCorpInProgress.owner_id;
      }

      if (blockSelfServiceSignup) {
        redirectToSignupBlockingPage();
      }
    } else if (isManagementPath) {
      // only premium admins cannot access basic corp management pages
      if (isPremiumAdmin && !isBasicAdmin) {
        redirectToCorpAdminSite();
        return;
      }

      if (isInternalAdmin) {
        try {
          const basicCorpData = await getCorpData(urlCorpId as string);
          corpData = basicCorpData;

          if (basicCorpData.signUpType === CORP_SIGN_UP_TYPE.PREMIUM) {
            // internal admin trying to access basic corp route with enterprise corp id
            redirectToHomePage(urlCorpId as string);
            return;
          }
        } catch (e) {
          // internal admin trying to access basic corp route without corp data. Ex: Invalid corp-id
          redirectToCorpAdminSite();
          return;
        }
      }

      // if basic admin is trying to access a corp that is not theirs, redirect to the first basic corp they have access to
      if (isBasicAdmin && !userHasAccessToCurrentBasicCorp) {
        reloadManagementPage(allCorpsUserIsAdminOf.BASIC[0].owner_id);
        return;
      }

      // non corp user trying to access management page should be redirect to signup flow
      if (isNonCorpUser) {
        const action = blockSelfServiceSignup
          ? redirectToGrubhubHomepage
          : redirectToSignupPage;
        action();

        return;
      }

      corpId = urlCorpId;
    } else if (isEnterpriseManagementPath) {
      // Trying to access enterprise corp admin routes.

      // non-corp user trying to access enterprise corp admin routes should be redirected to GrubhubHomepage
      if (isNonCorpUser) {
        redirectToGrubhubHomepage();
        return;
      }

      try {
        corpData = corpData || (await getCorpData(urlCorpId as string));
        corpId = urlCorpId;
      } catch (error) {
        corpData = null;
      }
      // User trying to access enterprise corp home route without corp data. Ex: Invalid corp-id
      if (!corpData) {
        if (isInternalAdmin) {
          // Internal Admin user will be redirected to corp selection page
          redirectToCorpAdminSite();
          return;
        }
        if (isPremiumAdmin) {
          // Enterprise corp admin user will be redirected to corp home with owner_id from the first premium corp
          redirectToHomePage(allCorpsUserIsAdminOf.PREMIUM[0].owner_id);
          return;
        }
        if (isBasicAdmin) {
          // Basic corp admin user will be redirected to management order-history with first owner_id from the first basic corp
          redirectToManagementPage(allCorpsUserIsAdminOf.BASIC[0].owner_id);
          return;
        }
      }

      if (isInternalAdmin && corpData.signUpType === CORP_SIGN_UP_TYPE.BASIC) {
        // internal admin trying to access enterprise corp route with basic corp id
        redirectToManagementPage(urlCorpId as string);
        return;
      }
    } else if (isCurrentPathInternalAdmin) {
      // non-corp user trying to access enterprise corp admin routes should be redirected to GrubhubHomepage
      if (isNonCorpUser) {
        redirectToGrubhubHomepage();
        return;
      }
      // Enterprise corp admin user trying to access internal admin route will be redirected to corp home
      // with owner_id from the first premium corp
      if (isPremiumAdmin) {
        redirectToHomePage(allCorpsUserIsAdminOf.PREMIUM[0].owner_id);
        return;
      }
      // Basic corp admin user trying to access internal admin route will be redirected to management with first owner_id from the first basic corp
      if (isBasicAdmin) {
        redirectToManagementPage(allCorpsUserIsAdminOf.BASIC[0].owner_id);
        return;
      }
    }

    setCorpId(corpId);
    setUser(user);
    setAllUserCorps(allCorpsUserIsAdminOf);

    const isFinancialAdmin = hasClaimForCurrentCorpId(
      CORP_CLAIMS.CORP_FINANCIAL_ADMIN,
      corpId
    );

    const isCorpAdminOnly =
      !isInternalAdmin &&
      !isFinancialAdmin &&
      hasClaimForCurrentCorpId(CORP_CLAIMS.CORP_ADMIN, corpId);

    setEnterpriseData({
      isInternalAdminPath: isCurrentPathInternalAdmin,
      isInternalAdminUser: isInternalAdmin,
      isFinancialAdminUser: isFinancialAdmin,
      isOnlyCorpAdminUser: isCorpAdminOnly,
      isParentAdminUser: isParentAdmin,
      isParentAdminPath: isCurrentPathParentAdmin,
    });

    try {
      if (corpId && !corpData) {
        corpData = await getCorpData(corpId);
      }
      setCorpData(corpData);
    } catch (error) {
      setHasAuthError(true);
      console.error('Error fetching corp data', error);
    }
    setIsUserAuthorizedForCurrentPath(true);
  }

  async function getAllUserCorpsBySignupType(
    userId: string
  ): Promise<AdminCorpsBySignupType> {
    const userCorpsResponse: { object: ICorpByRole[]; status: string } =
      await sdk.http.get(`/corp-admin/corp/diner/${userId}`, {
        searchParams: {
          filterByRoles: [
            CORP_USER_ROLE.CORP_ADMIN,
            CORP_USER_ROLE.CORP_FINANCIAL_ADMIN,
            CORP_USER_ROLE.CORP_ADMIN_AND_FINANCIAL_ADMIN,
            CORP_USER_ROLE.CORP_PARENT_ADMIN,
          ],
        },
      });

    const { object: allUserCorps } = userCorpsResponse || {
      object: [] as ICorpByRole[],
    };

    const mappedUserCorps = allUserCorps.reduce(
      (mappedCorps, corp) => {
        mappedCorps[corp.sign_up_type].push(corp);
        return mappedCorps;
      },
      {
        PREMIUM: [],
        BASIC: [],
      } as AdminCorpsBySignupType
    );

    return mappedUserCorps;
  }

  function hasClaimForCurrentCorpId(
    claimName: CORP_CLAIMS,
    corpId: string
  ): boolean {
    const claimsList = get(sdk, 'data', 'auth', 'credentials', 'claims') || [];

    return claimsList.some(
      (claim) => claim.claim === claimName && claim.claim_id === corpId
    );
  }

  function getCorpAndFinancialAdminClaims(): Claim[] {
    if (sdk.data.auth.credentials.claims) {
      return sdk.data.auth.credentials.claims.filter(
        (claim) =>
          claim.claim === CORP_CLAIMS.CORP_ADMIN ||
          claim.claim === CORP_CLAIMS.CORP_FINANCIAL_ADMIN
      );
    }

    return [];
  }

  function isCorpIdInArray(
    corpArray: ICorpByRole[] = [],
    corpId: string
  ): boolean {
    return corpArray.some((corp) => {
      if (corp.sign_up_type === CORP_SIGN_UP_TYPE.PREMIUM) {
        return corp.owner_id === corpId;
      }

      // we don't want to include BASIC accounts with signup in progress for redirect purposes
      if (
        corp.sign_up_type === CORP_SIGN_UP_TYPE.BASIC &&
        corp.account_status === BASIC_ACCOUNT_STATUS.ACTIVE
      ) {
        return corp.owner_id === corpId;
      }

      return false;
    });
  }

  async function isUserSignedIn(): Promise<boolean> {
    try {
      // in the event that your session was valid, however you for some reason have been disabled from the backend
      // we need to check that the local credentials are still valid with the backend, as checking dates is not gonna be good
      // enough
      const sessionData = await sdk.rest.auth.session();

      return !!get(sessionData, 'credential', 'ud_id');
    } catch (e) {
      await signOut();
      return false;
    }
  }

  async function getCorpData(corpId: string): Promise<CorpUIResponse> {
    try {
      const response = await sdk.http.get(`/corps/${corpId}`);
      return camelizeResponseData(response);
    } catch (error) {
      return Promise.reject();
    }
  }

  function redirectToSignInPage() {
    window.location.href = `${window.location.origin}/login?redirectUrl=${window.location.origin}${asPath}&reload=true`;
  }

  function redirectToSignupPage() {
    push('/corporate/signup/step/1');
  }

  function redirectToSignupBlockingPage() {
    push('/corporate/signup/info');
  }

  function redirectToCorpAdminSite() {
    window.location.href = `${window.location.origin}/groupadmin/`;
  }

  function redirectToGrubhubHomepage() {
    window.location.href = window.location.origin;
  }

  function redirectToManagementPage(corpId: string) {
    push(`/corporate/management/${corpId}/home${window.location.search}`);
  }

  function reloadManagementPage(corpId: string) {
    // reloading page with new corpId, refreshing auth state
    window.location.href = `${window.location.origin}/corporate/management/${corpId}/home${window.location.search}`;
  }

  function redirectToHomePage(corpId: string) {
    push(getHomePath(corpId));
  }

  async function signOut() {
    await sdk.rest.auth.logout();
    redirectToSignInPage();
  }

  useEffect(() => {
    if (isReady) {
      checkUserAuthentication();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, isReady]);

  if (isUserAuthorizedForCurrentPath) {
    return (
      <AuthContext.Provider
        value={{
          user,
          corpId,
          corpData,
          signOut,
          checkUserAuthentication,
          redirectToCorpAdminSite,
          enterpriseData,
          redirectToHomePage,
          allUserCorps,
          hasAuthError,
        }}
      >
        {children}
      </AuthContext.Provider>
    );
  }

  return null;
}

const useAuth = (): IAuthContext => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export { AuthProvider, useAuth };
